import { set } from 'lodash';
import filesService from "@/features/file-management/pages/FilePage/services/files.service";
import { AnalyticService } from "@/features/file-management/services/AnalyticService";
import AuthService from "@/features/auth/services/AuthService";
import { UploadFileService } from "@/features/upload/services/UploadFileService";
import { uploadSwClient } from "@/features/file-management/pages/FilePage/uploadManager/UploadServiceWorkerClient";
import { setBootstrapConfig } from "@/api/HttpClient";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import { ajax } from "rxjs/ajax";
import { filter, map, switchMap, mergeMap } from "rxjs/operators";
import { from, of } from "rxjs";
import type {
  BootstrapState,
  RootEpic,
  SystemConfig,
} from "../../common/type-state";
import { FolderService } from "../../services/FolderService";
import { ConversionApi } from "@/api/conversion.api";
import { WorkflowApi } from "@/api/workflow.api";
import projectsService from "@/features/projects/services/projectsApi";
import drawingsService from "@/features/drawings/services/DrawingsService";
import ExtractApi from "@/features/file-management/pages/FilePage/services/extractionFile.service";
import deleteFileService from "../deleteFile/deleteFileService";
import archiveFileService from "@/features/file-management/stores/archiveFile/archiveFileService";
import { DriveService } from "@/features/drive/services/DriveService";
import { StorageService } from "@/features/settings/components/StorageManagement/services/storageApi";
import { WorkflowAnalyticService } from '@/features/workflow/services/WorkflowAnalyticService';
import { setConfig as setFirebaseConfig } from "@/libs/analytics/firebase";
import { setConfig as setOpenReplayConfig } from "@/libs/analytics/openreplay";

console.log("-=======================", window.location.origin);
const PATH_SYSTEM_CONFIG = `${
  window.location.origin || ""
}/assets/system-config.json`;
const USE_DEFAULT_CONFIG = import.meta.env.VITE_USE_DEFAULT_CONFIG === "true";

const DEFAULT_CONFIG: SystemConfig = {
  protocol: import.meta.env.VITE_HOST_PROTOCOL || "http",
  hostStream: import.meta.env.VITE_HOST_STREAM || "/",
  hostFile: import.meta.env.VITE_HOST_FILE || "",
  hostConversion: import.meta.env.VITE_HOST_CONVERSION || "/",
  hostBim: import.meta.env.VITE_HOST_BIM || "/",
  hostIdentity: import.meta.env.VITE_HOST_IDENTITY || "/",
  modeRender: (import.meta.env.VITE_MODE_RENDER as "csr" | "ssr") || "csr",
  baseName: import.meta.env.VITE_BASENAME || "/",
  serverDomain: import.meta.env.VITE_SERVER_DOMAIN || "/",
  hostDOCAI: import.meta.env.VITE_HOST_DOCAI || "/",
  File: import.meta.env.VITE_FILE || "/",
  authenUrl: import.meta.env.VITE_AUTHEN_URL || "https://localhost:7059",
  hostApp: import.meta.env.VITE_HOST_APP || "https://api.inertia.hicas.vn",
  hostAnalysis: import.meta.env.VITE_HOST_ANALYSIS || "",
  viewPDF: import.meta.env.VITE_VIEW_PDF || "https://docai.inertia.hicas.vn",
  hostSignalR: import.meta.env.VITE_HOST_SIGNALR || "http://10.0.0.105:8001",
  chatbot: import.meta.env.VITE_CHATBOT || "",
  hostProject: import.meta.env.VITE_HOST_PROJECT || "",
  hostWorkflow: import.meta.env.VITE_HOST_WORKFLOW || "https://dev-wm.hicas.vn",
  tenant: import.meta.env.VITE_TENANT || "hicas", // Default tenant
  oAuth: {
    clientId: import.meta.env.VITE_OAUTH_CLIENT_ID || "CXM",
    clientSecret: import.meta.env.VITE_OAUTH_CLIENT_SECRET || "ConstruxivViewersecret",
    scope: import.meta.env.VITE_OAUTH_SCOPE || "offline_access API",
  },
  firebase: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "",
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
    appId: import.meta.env.VITE_FIREBASE_APP_ID || "",
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "",
  },
  openReplay: {
    projectKey: import.meta.env.VITE_OPENREPLAY_PROJECT_KEY || "",
    ingestPoint: import.meta.env.VITE_OPENREPLAY_INGEST_POINT || undefined,
  },
};

const initialStateBootstrap: BootstrapState = {
  systemConfig: DEFAULT_CONFIG,
  isSuccess: false,
};

const updateHostService = async (config: SystemConfig) => {
  setBootstrapConfig(config);
  FolderService.setConfig(config);
  AnalyticService.setConfig(config);
  AuthService.setConfig(config);
  ConversionApi.setConfig(config);
  UploadFileService.setConfig(config);
  filesService.setConfig(config);
  projectsService.setConfig(config);
  drawingsService.setConfig(config);
  ExtractApi.setConfig(config);
  deleteFileService.setConfig(config);
  archiveFileService.setConfig(config);
  projectsService.setConfig(config);
  DriveService.setConfig(config);
  WorkflowApi.setConfig(config);
  StorageService.setConfig(config);
  WorkflowAnalyticService.setConfig(config);
  setFirebaseConfig(config);
  setOpenReplayConfig(config);
  console.log("[bootstrap] Host services updated with new config");
  
  // Register and configure upload service worker with the endpoint from system-config
  if (uploadSwClient && config.File) {
    try {
      await uploadSwClient.register();
      await uploadSwClient.configure({ endpoint: config.File });
      console.log("[bootstrap] Upload service worker configured with endpoint:", config.File);
    } catch (err) {
      console.warn("[bootstrap] Failed to configure upload service worker endpoint", err);
    }
  } else {
    console.warn("[bootstrap] Upload service worker client not available or File endpoint missing in config");
  }
};

const bootstrap$: RootEpic = (action$) =>
  action$.pipe(
    filter(fetchConfig.match),
    switchMap(() => {
      console.log(
        "[bootstrap$] Fetching system-config.json from:",
        PATH_SYSTEM_CONFIG
      );
      return ajax
        .getJSON(PATH_SYSTEM_CONFIG, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        } as any)
        .pipe(
          mergeMap((res) => {
            console.log("[bootstrap$] system-config.json loaded:", res);
            const config = USE_DEFAULT_CONFIG
              ? DEFAULT_CONFIG
              : { ...DEFAULT_CONFIG, ...(res as SystemConfig) };
            console.log("[bootstrap$] Final config to set:", config);
            
            // Convert the async updateHostService call to an Observable
            return from(updateHostService(config)).pipe(
              map(() => bootstrapSlice.actions.setSystemConfig(config))
            );
          })
        );
    })
  );

const bootstrapSlice = createSlice({
  name: "bootstrap",
  initialState: initialStateBootstrap,
  reducers: {
    setSystemConfig: (state, action: PayloadAction<SystemConfig>) => {
      state.systemConfig = action.payload;
      state.isSuccess = true;
    },
    fetchConfig: (state) => {
      state.isSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state) => {
      state.isSuccess = false;
    });
  },
});

export const BootstrapEpics = [bootstrap$];
export const { fetchConfig } = bootstrapSlice.actions;
export const bootstrapReducer = bootstrapSlice.reducer;
